import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import Typography from "../../../../components/Typography/Typography";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { Button } from "../../../../components/Buttons";
import IconComponent from "../../../../components/Icon/IconComponent";
import CustomCreditModal from "./CustomCreditModal";
import {
  BRAND,
  GREY,
  BASE,
  PRIMARY,
  BORDER,
  SUCCESS,
} from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { NO_RECORD_FOUND_IMAGE } from "../../../../utils/constants/assets";

const BuyCredits = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);

  const styles = {
    container: {
      margin: "16px 0px 0px 16px",
      borderRadius: "6px",
      height: "100%",
    },
    mainWrapper: {
      background: BASE.WHITE,
      height: "100%",
      overflow: "auto",
      borderRadius: "6px",
    },
    header: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      borderBottom: `1px solid ${BORDER.DEFAULT}`,
      width: "100%",
    },
    contentWrapper: {
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    },
    balanceCard: {
      background: `linear-gradient(135deg, ${BRAND.PRIMARY} 0%, ${PRIMARY.W_800} 100%)`,
      borderRadius: "12px",
      padding: "24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      boxShadow: `0 4px 12px rgba(255, 72, 72, 0.2)`,
    },
    balanceInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    balanceAmount: {
      fontSize: "32px",
      fontWeight: fontWeight.BOLD,
      color: BASE.WHITE,
    },
    balanceLabel: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: "rgba(255, 255, 255, 0.9)",
    },
    packagesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: "20px",
    },
    packageCard: {
      background: BASE.WHITE,
      border: `1px solid ${BORDER.DEFAULT}`,
      borderRadius: "12px",
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
    },
    packageCardHover: {
      background: BASE.WHITE,
      border: `2px solid ${BRAND.PRIMARY}`,
      borderRadius: "12px",
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
      transform: "translateY(-4px)",
      boxShadow: `0 8px 24px rgba(255, 72, 72, 0.15)`,
    },
    customCard: {
      background: `linear-gradient(135deg, ${BASE.BACKGROUND_LIGHT} 0%, ${BASE.BACKGROUND} 100%)`,
      border: `2px dashed ${BORDER.DEFAULT}`,
      borderRadius: "12px",
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      minHeight: "320px",
    },
    customCardHover: {
      background: `linear-gradient(135deg, ${PRIMARY.W_50} 0%, ${PRIMARY.W_100} 10% ARCH_WHITE 100%)`, // Mixing white for subtleness
      background: `linear-gradient(135deg, ${PRIMARY.W_50} 0%, ${BASE.WHITE} 100%)`,
      border: `2px dashed ${BRAND.PRIMARY}`,
      borderRadius: "12px",
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      minHeight: "320px",
      transform: "translateY(-4px)",
    },
    addIcon: {
      fontSize: "64px",
      color: GREY.TERTIARY,
    },
    addIconHover: {
      fontSize: "64px",
      color: BRAND.PRIMARY,
    },
    popularBadge: {
      position: "absolute",
      top: "16px",
      right: "-32px",
      background: BRAND.PRIMARY,
      color: BASE.WHITE,
      padding: "4px 40px",
      fontSize: "12px",
      fontWeight: fontWeight.SEMIBOLD,
      transform: "rotate(45deg)",
    },
    creditAmount: {
      fontSize: "28px",
      fontWeight: fontWeight.BOLD,
      color: GREY.PRIMARY,
    },
    priceWrapper: {
      display: "flex",
      alignItems: "baseline",
      gap: "8px",
    },
    price: {
      fontSize: "36px",
      fontWeight: fontWeight.BOLD,
      color: BRAND.PRIMARY,
    },
    priceLabel: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
    },
    featuresList: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginTop: "8px",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    checkIcon: {
      fontSize: "18px",
      color: SUCCESS.W_500,
    },
    featureText: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
    },
    buttonWrapper: {
      marginTop: "auto",
    },
    customTitle: {
      fontSize: "24px",
      fontWeight: fontWeight.BOLD,
      color: GREY.PRIMARY,
      textAlign: "center",
    },
    customDescription: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
      textAlign: "center",
      maxWidth: "250px",
    },
    emptyStateContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      gap: "16px",
      padding: "40px",
    },
    emptyTitle: {
      fontSize: "20px",
      fontWeight: fontWeight.SEMIBOLD,
      color: GREY.PRIMARY,
    },
    emptyDescription: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
      textAlign: "center",
      maxWidth: "400px",
    },
  };

  const creditPackages = [
    {
      id: 1,
      credits: "5,000",
      price: 199,
      features: [
        "Email verification included",
        "Priority support",
        "Valid for 12 months",
        "Unused credits rollover",
        "Bulk export available",
      ],
      popular: true,
    },
    {
      id: 2,
      credits: "10,000",
      price: 349,
      features: [
        "Email verification included",
        "Premium support",
        "Valid for 12 months",
        "Unused credits rollover",
        "Bulk export available",
        "API access",
      ],
      popular: false,
    },
    {
      id: 3,
      credits: "25,000",
      price: 799,
      features: [
        "Email verification included",
        "Dedicated support",
        "Valid for 12 months",
        "Unused credits rollover",
        "Bulk export available",
        "API access",
        "Custom integrations",
      ],
      popular: false,
    },
  ];

  const userDetailsSelector = useSelector((state) => state?.userDetails);
  const userDetails = userDetailsSelector?.payload;
  const currentBalance = userDetails?.available_credits || 0;
  const [customCardHovered, setCustomCardHovered] = useState(false);

  return (
    <Box style={styles.container}>
      <Box style={styles.mainWrapper}>
        <Box style={styles.header}>
          <Typography
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label="Buy Credits"
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="Purchase credits to unlock contacts and enrich your data"
          />
        </Box>

        <Box style={styles.contentWrapper}>
          {/* Current Balance Card */}
          <Box style={styles.balanceCard}>
            <Box style={styles.balanceInfo}>
              <span style={styles.balanceLabel}>Current Balance</span>
              <span style={styles.balanceAmount}>
                {currentBalance.toLocaleString()}
              </span>
              <span style={styles.balanceLabel}>credits available</span>
            </Box>
            <IconComponent
              iconLabel={ICON_LABELS.ACCOUNT_BALANCE_WALLET}
              style={{ fontSize: "48px", color: "rgba(255, 255, 255, 0.3)" }}
            />
          </Box>

          {creditPackages.length === 0 ? (
            <Box style={styles.emptyStateContainer}>
              <img
                src={NO_RECORD_FOUND_IMAGE}
                alt="Not Found"
                style={{ width: "200px", height: "auto" }}
              />
              <span style={styles.emptyTitle}>No Packages Found</span>
              <span style={styles.emptyDescription}>
                There are currently no credit packages available. Please check
                back later or contact support for assistance.
              </span>
            </Box>
          ) : (
            <Box style={styles.packagesGrid}>
              {creditPackages.map((pkg) => (
                <Box
                  key={pkg.id}
                  style={
                    selectedPackage === pkg.id
                      ? styles.packageCardHover
                      : styles.packageCard
                  }
                  onClick={() => setSelectedPackage(pkg.id)}
                  onMouseEnter={(e) => {
                    if (selectedPackage !== pkg.id) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.08)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedPackage !== pkg.id) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  {pkg.popular && (
                    <Box style={styles.popularBadge}>POPULAR</Box>
                  )}

                  <Box style={styles.creditAmount}>{pkg.credits} Credits</Box>

                  <Box style={styles.priceWrapper}>
                    <span style={styles.price}>${pkg.price}</span>
                    <span style={styles.priceLabel}>/month</span>
                  </Box>

                  <Box style={styles.featuresList}>
                    {pkg.features.map((feature, index) => (
                      <Box key={index} style={styles.featureItem}>
                        <IconComponent
                          iconLabel={ICON_LABELS.CHECK_CIRCLE}
                          style={styles.checkIcon}
                        />
                        <span style={styles.featureText}>{feature}</span>
                      </Box>
                    ))}
                  </Box>

                  <Box style={styles.buttonWrapper}>
                    <Button
                      label={
                        selectedPackage === pkg.id
                          ? "Selected"
                          : "Select Package"
                      }
                      size={"md40"}
                      variant={selectedPackage === pkg.id ? "primary" : "ghost"}
                    />
                  </Box>
                </Box>
              ))}

              {/* Custom Credit Card */}
              <Box
                style={
                  customCardHovered ? styles.customCardHover : styles.customCard
                }
                onClick={() => setIsCustomModalOpen(true)}
                onMouseEnter={() => setCustomCardHovered(true)}
                onMouseLeave={() => setCustomCardHovered(false)}
              >
                <IconComponent
                  iconLabel={ICON_LABELS.ADD_CIRCLE}
                  style={
                    customCardHovered ? styles.addIconHover : styles.addIcon
                  }
                />
                <span style={styles.customTitle}>Custom Package</span>
                <span style={styles.customDescription}>
                  Need a different amount? Create your own custom credit package
                </span>
                <Box style={{ width: "100%", marginTop: "auto" }}>
                  <Button
                    label="Customize"
                    size={"md40"}
                    variant={customCardHovered ? "primary" : "ghost"}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Custom Credit Modal */}
        <CustomCreditModal
          isOpen={isCustomModalOpen}
          onClose={() => setIsCustomModalOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default BuyCredits;
